import { useCallback, useEffect, useRef, useState } from "react";

// A tiny undo/redo history hook. Holds up to `max` past states.
// The trick is snapshotting on *committed* changes, not every keystroke,
// so we expose `commit(newState)` and `set(state)` separately:
//   • `set(state)` — mutate in-place (for typing) without pushing history
//   • `commit(state)` — push previous state onto the undo stack, then set
// Debounced commits from the caller (e.g. 400ms after last edit) give a
// natural "one Cmd-Z per idea" feel.
export function useHistory(initial, { max = 50 } = {}) {
  const [state, _setState] = useState(initial);
  const past = useRef([]);   // stack of prior states (most recent last)
  const future = useRef([]); // states redone into if user undoes

  // sync when parent replaces state entirely (e.g. fresh load from server)
  const seedRef = useRef(initial);
  useEffect(() => {
    if (initial !== seedRef.current) {
      seedRef.current = initial;
      _setState(initial);
      past.current = [];
      future.current = [];
    }
  }, [initial]);

  const set = useCallback((next) => {
    _setState((prev) => (typeof next === "function" ? next(prev) : next));
  }, []);

  const commit = useCallback((next) => {
    _setState((prev) => {
      const nextVal = typeof next === "function" ? next(prev) : next;
      // Only push if it's actually different (JSON compare is coarse but cheap
      // for our page-size payloads and avoids duplicate entries)
      if (JSON.stringify(prev) === JSON.stringify(nextVal)) return prev;
      past.current.push(prev);
      if (past.current.length > max) past.current.shift();
      future.current = [];
      return nextVal;
    });
  }, [max]);

  const undo = useCallback(() => {
    if (past.current.length === 0) return null;
    let restored = null;
    _setState((prev) => {
      const prior = past.current.pop();
      future.current.push(prev);
      restored = prior;
      return prior;
    });
    return restored;
  }, []);

  const redo = useCallback(() => {
    if (future.current.length === 0) return null;
    let restored = null;
    _setState((prev) => {
      const next = future.current.pop();
      past.current.push(prev);
      restored = next;
      return next;
    });
    return restored;
  }, []);

  const canUndo = past.current.length > 0;
  const canRedo = future.current.length > 0;

  return { state, set, commit, undo, redo, canUndo, canRedo };
}
